<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import MessageApi from 'dashboard/api/inbox/message';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const store = useStore();
const expanded = ref(false);

// Cancel modal
const showCancelModal = ref(false);
const messageToCancel = ref(null);

// Edit modal
const showEditModal = ref(false);
const editingMessage = ref(null);
const editContent = ref('');
const editDate = ref('');
const editTime = ref('');
const editSaving = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat || {});
const scheduledMessages = computed(() => {
  const messages = currentChat.value.messages || [];
  return messages.filter(m => m.status === 'scheduled' && m.content_attributes?.scheduled_at);
});
const count = computed(() => scheduledMessages.value.length);

const contactName = computed(() => {
  const contact = currentChat.value.meta?.sender || {};
  return contact.name || contact.available_name || '';
});
const contactPhone = computed(() => {
  const contact = currentChat.value.meta?.sender || {};
  const raw = contact.phone_number || '';
  if (!raw || raw.length < 10) return raw;
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length === 13) {
    return '+' + digits.slice(0,2) + ' (' + digits.slice(2,4) + ') ' + digits.slice(4,9) + '-' + digits.slice(9);
  }
  return raw;
});

function formatDate(value) {
  if (!value) return '';
  const d = typeof value === 'number' && value < 1e12 ? new Date(value * 1000) : new Date(value);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '';
  return String(d.getDate()).padStart(2,'0') + '/' + String(d.getMonth()+1).padStart(2,'0') + '/' + d.getFullYear() + ' - ' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0');
}

function getCreatedDate(msg) { return msg.created_at || msg.timestamp || ''; }
function senderName(msg) { return msg.sender?.name || msg.sender?.available_name || 'Agente'; }
function senderAvatar(msg) { return msg.sender?.avatar_url || msg.sender?.thumbnail || ''; }
function senderInitial(msg) { return (senderName(msg)).charAt(0).toUpperCase(); }

// Cancel
function openCancelModal(message) { messageToCancel.value = message; showCancelModal.value = true; }
function closeCancelModal() { showCancelModal.value = false; messageToCancel.value = null; }
async function confirmCancel() {
  if (!messageToCancel.value) return;
  try { await store.dispatch('deleteMessage', { conversationId: props.conversationId, messageId: messageToCancel.value.id }); } catch (e) { /* silent */ }
  closeCancelModal();
}

// Edit
function openEditModal(msg) {
  editingMessage.value = msg;
  editContent.value = msg.content || '';
  const sa = msg.content_attributes?.scheduled_at;
  if (sa) {
    const d = new Date(sa);
    editDate.value = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    editTime.value = String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0');
  } else { editDate.value = ''; editTime.value = ''; }
  showEditModal.value = true;
}
function closeEditModal() { showEditModal.value = false; editingMessage.value = null; editSaving.value = false; }

async function confirmEdit() {
  if (!editingMessage.value || !editContent.value.trim()) return;
  editSaving.value = true;
  try {
    const scheduledAt = editDate.value && editTime.value
      ? new Date(editDate.value + 'T' + editTime.value + ':00').toISOString() : null;
    const { data } = await MessageApi.update(props.conversationId, editingMessage.value.id, { content: editContent.value, scheduled_at: scheduledAt });
    store.dispatch('updateMessage', data);
    closeEditModal();
  } catch (e) { console.error('Failed to update scheduled message', e); editSaving.value = false; }
}

async function deleteFromEdit() {
  if (!editingMessage.value) return;
  try { await store.dispatch('deleteMessage', { conversationId: props.conversationId, messageId: editingMessage.value.id }); } catch (e) { /* silent */ }
  closeEditModal();
}

const calendarIcon = '/static/img/sz-calendar.svg';
const pencilIcon = '/static/img/sz-pencil.svg';
const trashIcon = '/static/img/sz-trash.svg';
</script>

<template>
  <div v-if="count > 0" class="sz-sched-section">
    <button class="sz-sched-header" @click="expanded = !expanded">
      <div class="sz-sched-header-left">
        <img :src="calendarIcon" alt="" class="sz-sched-header-icon" />
        <span>Mensagens agendadas</span>
      </div>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="sz-sched-chevron" :class="{ 'sz-chevron-open': expanded }">
        <path d="M7.5 9.5L12 14.5L16.5 9.5" stroke="#262626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <div v-if="expanded" class="sz-sched-list">
      <div v-for="msg in scheduledMessages" :key="msg.id" class="sz-sched-item">
        <div class="sz-sched-sender">
          <img v-if="senderAvatar(msg)" :src="senderAvatar(msg)" :alt="senderName(msg)" class="sz-sched-avatar" />
          <span v-else class="sz-sched-avatar-fallback">{{ senderInitial(msg) }}</span>
          <span class="sz-sched-sender-name">{{ senderName(msg) }}</span>
        </div>
        <div class="sz-sched-content-row">
          <p class="sz-sched-text">{{ msg.content }}</p>
          <div class="sz-sched-actions">
            <button class="sz-sched-action-btn" title="Editar" @click="openEditModal(msg)">
              <img :src="pencilIcon" alt="Editar" class="sz-sched-action-icon" />
            </button>
            <button class="sz-sched-action-btn" title="Excluir" @click="openCancelModal(msg)">
              <img :src="trashIcon" alt="Excluir" class="sz-sched-action-icon" />
            </button>
          </div>
        </div>
        <div class="sz-sched-dates-row">
          <span class="sz-sched-date-item">
            <img :src="'/static/img/sz-calendar-2.svg'" alt="" class="sz-sched-date-icon" />
            {{ formatDate(getCreatedDate(msg)) }}
          </span>
          <span class="sz-sched-date-item">
            <img :src="'/static/img/sz-mail-send.svg'" alt="" class="sz-sched-date-icon" />
            {{ formatDate(msg.content_attributes?.scheduled_at) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Cancel Modal -->
    <Teleport to="body">
      <div v-if="showCancelModal" class="sz-cancel-overlay" @click.self="closeCancelModal">
        <div class="sz-cancel-modal">
          <div class="sz-cancel-header">
            <h3>Confirmar exclusao da mensagem agendada</h3>
            <button class="sz-cancel-close" @click="closeCancelModal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="sz-cancel-body">
            <p>Voce tem certeza que deseja excluir a mensagem agendada? Essa mensagem sera excluida <strong>permanentemente</strong>.</p>
          </div>
          <div class="sz-cancel-footer">
            <button class="sz-cancel-btn-confirm" @click="confirmCancel">Sim, excluir mensagem</button>
            <button class="sz-cancel-btn-keep" @click="closeCancelModal">Nao, manter mensagem</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Modal -->
    <Teleport to="body">
      <div v-if="showEditModal" class="sz-edit-overlay" @click.self="closeEditModal">
        <div class="sz-edit-modal">
          <div class="sz-edit-header">
            <h3>Editar mensagem agendada</h3>
            <button class="sz-edit-close" @click="closeEditModal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="sz-edit-body">
            <div class="sz-edit-contact">
              <div class="sz-edit-contact-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A1A1A1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div class="sz-edit-contact-info">
                <div class="sz-edit-contact-name">{{ contactName }}</div>
                <div class="sz-edit-contact-phone">{{ contactPhone }}</div>
              </div>
            </div>
            <div class="sz-edit-field">
              <label class="sz-edit-label"><span class="sz-edit-required">*</span> Mensagem</label>
              <textarea v-model="editContent" class="sz-edit-textarea" rows="2" placeholder="Digite a mensagem..."></textarea>
            </div>
            <div class="sz-edit-datetime-row">
              <div class="sz-edit-field sz-edit-half">
                <label class="sz-edit-label">Data de envio</label>
                <input v-model="editDate" type="date" class="sz-edit-input" />
              </div>
              <div class="sz-edit-field sz-edit-half">
                <label class="sz-edit-label">Horario do envio</label>
                <input v-model="editTime" type="time" class="sz-edit-input" />
              </div>
            </div>
          </div>
          <div class="sz-edit-footer">
            <button class="sz-edit-btn-delete" @click="deleteFromEdit">Excluir</button>
            <div class="sz-edit-footer-right">
              <button class="sz-edit-btn-cancel" @click="closeEditModal">Cancelar</button>
              <button class="sz-edit-btn-confirm" :disabled="editSaving || !editContent.trim()" @click="confirmEdit">
                {{ editSaving ? 'Salvando...' : 'Confirmar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.sz-sched-section { border-bottom: 1px solid var(--color-border-light, #e5e7eb); transition: background 0.15s; }
.sz-sched-section:hover { background: #fafafa; }

/* Header */
.sz-sched-header { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 12px 16px; border: none; background: transparent; cursor: pointer; font-family: Inter, system-ui, sans-serif; }
.sz-sched-header-left { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; color: #000000; }
.sz-sched-header-icon { width: 24px; height: 24px; flex-shrink: 0; }
.sz-sched-chevron { color: #262626; transition: transform 0.2s; flex-shrink: 0; }
.sz-chevron-open { transform: rotate(180deg); }

/* List */
.sz-sched-list { padding: 0 16px 12px; display: flex; flex-direction: column; gap: 8px; }

/* Item card — no green background */
.sz-sched-item { display: flex; flex-direction: column; padding: 8px; gap: 4px; border: 1px solid var(--color-border-light, #e5e7eb); border-radius: 8px; }

/* Sender row */
.sz-sched-sender { display: flex; align-items: center; gap: 6px; min-width: 0; }

/* Content + actions row */
.sz-sched-content-row { display: flex; align-items: center; gap: 4px; width: 100%; }
.sz-sched-avatar { width: 20px; height: 20px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.sz-sched-avatar-fallback { width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; background: #388E3C; color: #fff; font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; }
.sz-sched-sender-name { font-weight: 500; font-size: 13px; line-height: 20px; color: #000000; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Message text */
.sz-sched-text { flex: 1; min-width: 0; font-size: 12px; line-height: 18px; color: #525252; margin: 0; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; }

/* Dates row */
.sz-sched-dates-row { display: flex; align-items: center; justify-content: space-between; }
.sz-sched-date-item { display: flex; align-items: center; gap: 3px; font-size: 11px; line-height: 16px; color: #525252; white-space: nowrap; }
.sz-sched-date-icon { width: 14px; height: 14px; flex-shrink: 0; }

/* Actions */
.sz-sched-actions { display: flex; flex-shrink: 0; }
.sz-sched-action-btn { padding: 4px; border-radius: 8px; border: none; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.sz-sched-action-btn:hover { background: rgba(0,0,0,0.06); }
.sz-sched-action-icon { width: 18px; height: 18px; }

/* Breakpoints */
@media (max-width: 1440px) {
  .sz-sched-sender-name { font-size: 12px; }
  .sz-sched-date-item { font-size: 10px; }
  .sz-sched-date-icon { width: 12px; height: 12px; }
  .sz-sched-text { font-size: 11px; }
}
@media (max-width: 1200px) {
  .sz-sched-sender-name { font-size: 11px; }
  .sz-sched-date-item { font-size: 9px; }
  .sz-sched-date-icon { width: 11px; height: 11px; }
}
</style>

<style>
/* Dark mode */
.dark .sz-sched-section { border-bottom-color: #363840; }
.dark .sz-sched-section:hover { background: rgba(255,255,255,0.03); }
.dark .sz-sched-header { background: transparent; }
.dark .sz-sched-header-left { color: #EDEEEF; }
.dark .sz-sched-header-icon { filter: invert(1); }
.dark .sz-sched-chevron path { stroke: #9CA3AF; }
.dark .sz-sched-item { background: transparent; border-color: #363840; }
.dark .sz-sched-sender-name { color: #EDEEEF; }
.dark .sz-sched-date-item { color: #9CA3AF; }
.dark .sz-sched-date-icon { filter: invert(0.6); }
.dark .sz-sched-text { color: #9CA3AF; }
.dark .sz-sched-action-icon { filter: invert(1); }
.dark .sz-sched-action-btn:hover { background: rgba(255,255,255,0.06); }

/* Cancel modal */
.sz-cancel-overlay { position: fixed; inset: 0; z-index: 99999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); }
.sz-cancel-modal { width: 480px; max-width: 95vw; background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); font-family: Inter, system-ui, sans-serif; overflow: hidden; }
.sz-cancel-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
.sz-cancel-header h3 { font-size: 18px; font-weight: 600; color: #1a1a1a; margin: 0; }
.sz-cancel-close { border: none; background: none; cursor: pointer; color: #9CA3AF; display: flex; }
.sz-cancel-body { padding: 16px 24px; }
.sz-cancel-body p { font-size: 14px; color: #525252; line-height: 1.6; margin: 0; }
.sz-cancel-body strong { color: #1a1a1a; }
.sz-cancel-footer { display: flex; gap: 12px; padding: 16px 24px 20px; }
.sz-cancel-btn-confirm { flex: 1; padding: 12px; border-radius: 8px; border: none; background: #dc2626; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; }
.sz-cancel-btn-confirm:hover { background: #b91c1c; }
.sz-cancel-btn-keep { flex: 1; padding: 12px; border-radius: 8px; border: 1px solid #D4D4D4; background: #fff; color: #1a1a1a; font-size: 14px; font-weight: 600; cursor: pointer; }
.sz-cancel-btn-keep:hover { background: #f5f5f5; }
.dark .sz-cancel-modal { background: #1F2125; }
.dark .sz-cancel-header h3 { color: #EDEEEF; }
.dark .sz-cancel-body p { color: #9CA3AF; }
.dark .sz-cancel-body strong { color: #EDEEEF; }
.dark .sz-cancel-btn-keep { background: #1F2125; border-color: #363840; color: #EDEEEF; }

/* Edit modal */
.sz-edit-overlay { position: fixed; inset: 0; z-index: 99999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); }
.sz-edit-modal { width: 493px; max-width: 95vw; background: #fff; border-radius: 8px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); font-family: Inter, system-ui, sans-serif; display: flex; flex-direction: column; }
.sz-edit-header { display: flex; align-items: center; justify-content: space-between; padding: 16px; }
.sz-edit-header h3 { font-size: 20px; font-weight: 500; color: #000; margin: 0; line-height: 28px; }
.sz-edit-close { border: none; background: none; cursor: pointer; color: #000; display: flex; padding: 4px; }
.sz-edit-body { display: flex; flex-direction: column; padding: 24px 16px; gap: 16px; border-top: 1px solid #D1D5DB; border-bottom: 1px solid #D1D5DB; }
.sz-edit-contact { display: flex; align-items: center; padding: 8px; gap: 8px; background: #FAFAFA; border-radius: 8px; }
.sz-edit-contact-avatar { width: 56px; height: 56px; border-radius: 999px; background: #F5F5F5; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sz-edit-contact-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sz-edit-contact-name { font-weight: 500; font-size: 16px; line-height: 24px; color: #000; }
.sz-edit-contact-phone { font-weight: 400; font-size: 12px; line-height: 20px; color: #5B5B5B; }
.sz-edit-field { display: flex; flex-direction: column; gap: 8px; }
.sz-edit-half { flex: 1; }
.sz-edit-label { font-weight: 500; font-size: 14px; line-height: 20px; color: #000; }
.sz-edit-required { color: #E7000B; }
.sz-edit-textarea { width: 100%; padding: 12px 16px; border: 1px solid #E5E5E5; border-radius: 8px; font-family: Inter, sans-serif; font-size: 16px; line-height: 24px; color: #525252; resize: none; outline: none; box-sizing: border-box; }
.sz-edit-textarea:focus { border-color: #388E3C; }
.sz-edit-datetime-row { display: flex; gap: 8px; }
.sz-edit-input { width: 100%; padding: 12px 16px; border: 1px solid #D1D5DB; border-radius: 8px; font-family: Inter, sans-serif; font-size: 16px; line-height: 24px; color: #262626; outline: none; box-sizing: border-box; }
.sz-edit-input:focus { border-color: #388E3C; }
.sz-edit-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px; }
.sz-edit-footer-right { display: flex; gap: 12px; }
.sz-edit-btn-delete { padding: 8px 16px; border: none; background: transparent; color: #E7000B; font-weight: 600; font-size: 14px; cursor: pointer; border-radius: 8px; }
.sz-edit-btn-delete:hover { background: #FEF2F2; }
.sz-edit-btn-cancel { padding: 8px 16px; border: 1.5px solid #D4D4D4; background: #fff; color: #000; font-weight: 600; font-size: 14px; cursor: pointer; border-radius: 8px; }
.sz-edit-btn-cancel:hover { background: #f5f5f5; }
.sz-edit-btn-confirm { padding: 8px 16px; border: none; background: #388E3C; color: #fff; font-weight: 600; font-size: 14px; cursor: pointer; border-radius: 8px; }
.sz-edit-btn-confirm:hover { background: #2E7D32; }
.sz-edit-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
.dark .sz-edit-modal { background: #1F2125; }
.dark .sz-edit-header h3 { color: #EDEEEF; }
.dark .sz-edit-close { color: #9CA3AF; }
.dark .sz-edit-body { border-color: #363840; }
.dark .sz-edit-contact { background: #2A2D31; }
.dark .sz-edit-contact-name { color: #EDEEEF; }
.dark .sz-edit-contact-phone { color: #9CA3AF; }
.dark .sz-edit-label { color: #EDEEEF; }
.dark .sz-edit-textarea { background: #2A2D31; border-color: #363840; color: #EDEEEF; }
.dark .sz-edit-input { background: #2A2D31; border-color: #363840; color: #EDEEEF; }
.dark .sz-edit-btn-cancel { background: #1F2125; border-color: #363840; color: #EDEEEF; }
.dark .sz-edit-btn-delete:hover { background: rgba(220,38,38,0.1); }
</style>
